import { requireOptionalNativeModule } from 'expo-modules-core'

/**
 * ASO-ATTR-001 — the JS face of the AdServices native module.
 *
 * ⚠️ `requireOptionalNativeModule`, NOT `requireNativeModule`, and that is the single most
 * important line in this file.
 *
 * This module is NATIVE code: it only exists in a binary produced by `eas build` after this branch
 * lands. Atlas ships most changes as OTA updates (`expo-updates`), so the JS below WILL run on
 * binaries that do not contain it — every currently installed copy of the app, for as long as
 * those installs live. The throwing variant would crash there; the optional one returns `null`, and
 * every caller here treats `null` as "this device cannot answer", which is the same branch web and
 * Android already take.
 *
 * So the contract is: an OTA that carries this code is inert on old binaries and active on new
 * ones, with no runtime version bump and no crash in between. Anything that changes that — a
 * top-level throwing require, a bare import of `AdServices` types — reintroduces the one failure
 * mode an OTA must never have. `utils/__tests__/appleAdServicesNativeConfig.test.js` guards it
 * structurally, because no test that mocks this module can see it.
 */
interface AtlasAdServicesNativeModule {
  getAttributionTokenAsync(): Promise<string>
  getRandomBytesAsync(count: number): Promise<number[]>
}

const nativeModule = requireOptionalNativeModule<AtlasAdServicesNativeModule>('AtlasAdServices')

/** Whether this binary can produce an Apple Ads attribution token at all. */
export function isAvailable(): boolean {
  return nativeModule != null
}

/**
 * Fetch a fresh Apple Ads attribution token.
 *
 * Resolves to `null` when the native module is absent (web, Android, or an iOS binary built before
 * this shipped). REJECTS when AdServices itself refused — the caller needs to tell those apart:
 * absence is permanent for this binary, a refusal may be a transient network error.
 */
export async function getAttributionTokenAsync(): Promise<string | null> {
  if (!nativeModule) return null
  return nativeModule.getAttributionTokenAsync()
}

/**
 * Cryptographically secure random bytes from the platform (`SecRandomCopyBytes`).
 *
 * Resolves to `null` where the native module is absent, so the caller can fall back explicitly and
 * SAY it fell back. It exists because the install id gates an authorisation (the link secret) and
 * Hermes has no `crypto.getRandomValues`; see the Swift for the full reasoning.
 */
export async function getRandomBytesAsync(count: number): Promise<Uint8Array | null> {
  if (!nativeModule || typeof nativeModule.getRandomBytesAsync !== 'function') return null
  const bytes = await nativeModule.getRandomBytesAsync(count)
  return Uint8Array.from(bytes)
}

export default { isAvailable, getAttributionTokenAsync, getRandomBytesAsync }
